/**
 * Parameterized authentication functions.
 *
 * Unlike CLI's client.ts, these take explicit params instead of reading from disk.
 * This allows both CLI and TUI to use them with their own config sources.
 */

import {
  createArbiClient,
  sealedBoxDecrypt,
  deriveEncryptionKeypairFromSigning,
  sealKeyForSession,
  base64ToBytes,
  type ArbiClient,
  type LoginResult,
} from '@arbidocs/client'
import { ArbiError } from './errors.js'
import type { CliConfig, CliCredentials, ConfigStore } from './config-types.js'
import { fetchSsoConfig, requestDeviceCode, pollForToken } from './device-flow.js'

// ── Types ────────────────────────────────────────────────────────────────────

export interface AuthenticatedClient {
  arbi: ArbiClient
  loginResult: LoginResult
}

export interface AuthContext {
  arbi: ArbiClient
  loginResult: LoginResult
  config: CliConfig
}

export interface WorkspaceContext extends AuthContext {
  workspaceId: string
  accessToken: string
}

/** Max client-side age for a cached access token. The server enforces
 *  the JWT's own exp; this is just a conservative ceiling so we don't
 *  hand a near-expired token to a long-running command. JWTs typically
 *  expire at 60min — 50min keeps us comfortably ahead. */
const TOKEN_MAX_AGE_MS = 50 * 60 * 1000

// ── Workspace choice formatting ──────────────────────────────────────────────

/** Minimal workspace shape required by formatWorkspaceChoices. */
interface WorkspaceForChoice {
  name: string
  external_id: string
  shared_document_count: number
  private_document_count: number
}

/**
 * Format a list of workspaces into prompt-friendly choices.
 * Returns `{ name, value, description }` objects suitable for @inquirer/prompts
 * or any similar select component.
 */
export function formatWorkspaceChoices(
  wsList: WorkspaceForChoice[]
): Array<{ name: string; value: string; description: string }> {
  return wsList.map((ws) => {
    const totalDocs = ws.shared_document_count + ws.private_document_count
    return {
      name: `${ws.name} (${totalDocs} docs)`,
      value: ws.external_id,
      description: ws.external_id,
    }
  })
}

// ── Functions ────────────────────────────────────────────────────────────────

/**
 * Build the standard ArbiClient options for a CLI/TUI consumer.
 *
 * Wires the file-backed session storage from `store` (when available)
 * into `@arbidocs/client` so the auto-relogin middleware can read the
 * stored signing key on a 401 and silently refresh the access token.
 * Browsers that pass a store without `getSessionStorageProvider` fall
 * back to the bundled IndexedDB adapter.
 */
function buildClientOptions(config: CliConfig, store?: ConfigStore, email?: string) {
  return {
    baseUrl: config.baseUrl,
    deploymentDomain: config.deploymentDomain,
    credentials: 'omit' as const,
    sessionStorage: store?.getSessionStorageProvider?.(),
    userEmail: email,
  }
}

/**
 * Create an SDK client, init sodium, and log in with stored signing key.
 * Returns the client + login result (which includes serverSessionKey).
 */
export async function createAuthenticatedClient(
  config: CliConfig,
  creds: CliCredentials,
  store: ConfigStore
): Promise<AuthenticatedClient> {
  const arbi = createArbiClient(buildClientOptions(config, store, creds.email))

  await arbi.crypto.initSodium()

  const signingPrivateKey = base64ToBytes(creds.signingPrivateKeyBase64)

  const loginResult = await arbi.auth.loginWithKey({
    email: creds.email,
    signingPrivateKey,
    ssoToken: creds.ssoToken,
  })

  // A fresh login = a fresh server session. The previous session's
  // workspace deposits do not transfer; clear the openedWorkspaces
  // mirror so the next workspace touch deposits properly into the new
  // session. Persist ``userExtId`` so the fast-path
  // (``buildAuthFromCache``) can restore the session identity for
  // features that need it (DM crypto, contact resolution).
  store.saveCredentials({
    ...creds,
    userExtId: loginResult.userExtId ?? creds.userExtId,
    serverSessionKeyBase64: arbi.crypto.bytesToBase64(loginResult.serverSessionKey),
    accessToken: undefined,
    tokenTimestamp: undefined,
    openedWorkspaces: undefined,
  })

  return { arbi, loginResult }
}

/**
 * Interactive (password-based) login flow.
 *
 * Creates an SDK client, initializes sodium, authenticates with email/password,
 * and persists the derived credentials to the config store.
 *
 * Use this for CLI/TUI login commands. For session recovery from stored keys,
 * use createAuthenticatedClient() instead.
 */
export async function performPasswordLogin(
  config: CliConfig,
  email: string,
  password: string,
  store: ConfigStore
): Promise<AuthContext> {
  const arbi = createArbiClient(buildClientOptions(config, store))

  await arbi.crypto.initSodium()

  const loginResult = await arbi.auth.login({ email, password })

  store.saveCredentials({
    email,
    userExtId: loginResult.userExtId,
    signingPrivateKeyBase64: arbi.crypto.bytesToBase64(loginResult.signingPrivateKey),
    serverSessionKeyBase64: arbi.crypto.bytesToBase64(loginResult.serverSessionKey),
    // `parent_ext_id` is set for persistent agent accounts (the user is
    // an Assistant owned by another user). Persisting it makes the CLI's
    // `arbi listen` work — it requires this to confirm a real agent
    // identity. Regular users get `null`, which we store as `undefined`.
    parentExtId: loginResult.parentExtId ?? undefined,
    // New session = no workspaces deposited yet.
    accessToken: undefined,
    tokenTimestamp: undefined,
    openedWorkspaces: undefined,
  })

  return { arbi, loginResult, config }
}

/**
 * Signing-key-based login flow (for agent recovery).
 *
 * Authenticates using a raw Ed25519 signing key instead of a password.
 * Used when an agent needs to re-login using a recovery key.
 */
export async function performSigningKeyLogin(
  config: CliConfig,
  email: string,
  signingPrivateKeyBase64: string,
  store: ConfigStore
): Promise<AuthContext> {
  const arbi = createArbiClient(buildClientOptions(config, store))

  await arbi.crypto.initSodium()

  const signingPrivateKey = base64ToBytes(signingPrivateKeyBase64)
  const loginResult = await arbi.auth.loginWithKey({ email, signingPrivateKey })

  store.saveCredentials({
    email,
    userExtId: loginResult.userExtId,
    signingPrivateKeyBase64,
    serverSessionKeyBase64: arbi.crypto.bytesToBase64(loginResult.serverSessionKey),
    parentExtId: loginResult.parentExtId ?? undefined,
    accessToken: undefined,
    tokenTimestamp: undefined,
    openedWorkspaces: undefined,
  })

  return { arbi, loginResult, config }
}

/**
 * SSO device-flow login (RFC 8628).
 *
 * Fetches SSO config from the deployment, initiates the device authorization
 * flow with Auth0, waits for the user to authorize in a browser, then logs
 * in to the ARBI deployment using the Auth0 JWT.
 */
export async function performSsoDeviceFlowLogin(
  config: CliConfig,
  email: string,
  password: string,
  store: ConfigStore,
  callbacks?: {
    onUserCode?: (userCode: string, verificationUri: string) => void
    onPoll?: (elapsedMs: number) => void
  }
): Promise<AuthContext> {
  // 1. Fetch SSO config from deployment
  const ssoConfig = await fetchSsoConfig(config.baseUrl)
  if (!ssoConfig.ssoEnabled) {
    throw new ArbiError('SSO is not enabled on this deployment')
  }

  // 2. Request device code from Auth0
  const dc = await requestDeviceCode(ssoConfig.domain, ssoConfig.clientId, ssoConfig.audience)

  // 3. Notify caller with the user code
  callbacks?.onUserCode?.(dc.user_code, dc.verification_uri_complete)

  // 4. Poll until user authorizes in browser
  const ssoToken = await pollForToken(
    ssoConfig.domain,
    ssoConfig.clientId,
    dc.device_code,
    dc.interval,
    dc.expires_in,
    callbacks?.onPoll
  )

  // 5. Log in to ARBI with SSO token + password
  const arbi = createArbiClient(buildClientOptions(config, store))
  await arbi.crypto.initSodium()
  const loginResult = await arbi.auth.login({ email, password, ssoToken })

  // 6. Persist credentials (same pattern as performPasswordLogin)
  //    Store ssoToken so session recovery can pass it to loginWithKey.
  store.saveCredentials({
    email,
    userExtId: loginResult.userExtId,
    signingPrivateKeyBase64: arbi.crypto.bytesToBase64(loginResult.signingPrivateKey),
    serverSessionKeyBase64: arbi.crypto.bytesToBase64(loginResult.serverSessionKey),
    parentExtId: loginResult.parentExtId ?? undefined,
    ssoToken,
    accessToken: undefined,
    tokenTimestamp: undefined,
    openedWorkspaces: undefined,
  })

  return { arbi, loginResult, config }
}

/**
 * Decrypt wrapped workspace key and generate the encrypted workspace key header.
 * Returns the encrypted key string for use with /open.
 */
export async function selectWorkspace(
  arbi: ArbiClient,
  workspaceId: string,
  wrappedKey: string,
  serverSessionKey: Uint8Array,
  signingPrivateKeyBase64: string
): Promise<string> {
  const signingPrivateKey = base64ToBytes(signingPrivateKeyBase64)

  const ed25519PublicKey = signingPrivateKey.slice(32, 64)
  const encryptionKeyPair = deriveEncryptionKeypairFromSigning({
    publicKey: ed25519PublicKey,
    secretKey: signingPrivateKey,
  })

  const workspaceKey = sealedBoxDecrypt(wrappedKey, encryptionKeyPair.secretKey)
  const header = await sealKeyForSession(workspaceKey, serverSessionKey)

  arbi.session.setSelectedWorkspace(workspaceId)
  return header
}

/**
 * Generate an encrypted workspace key for the given workspace.
 * Used for operations that need a key for a workspace other than the current one
 * (e.g., copy documents to a target workspace).
 */
export async function generateEncryptedWorkspaceKey(
  arbi: ArbiClient,
  wrappedKey: string,
  serverSessionKey: Uint8Array,
  signingPrivateKeyBase64: string
): Promise<string> {
  const signingPrivateKey = base64ToBytes(signingPrivateKeyBase64)
  const ed25519PublicKey = signingPrivateKey.slice(32, 64)
  const encryptionKeyPair = deriveEncryptionKeypairFromSigning({
    publicKey: ed25519PublicKey,
    secretKey: signingPrivateKey,
  })

  const workspaceKey = sealedBoxDecrypt(wrappedKey, encryptionKeyPair.secretKey)
  return sealKeyForSession(workspaceKey, serverSessionKey)
}

/**
 * Generate a random workspace key encrypted with the session public key.
 * Used when creating new workspaces.
 */
export async function generateNewWorkspaceKey(
  arbi: ArbiClient,
  serverSessionKey: Uint8Array
): Promise<string> {
  await arbi.crypto.initSodium()
  const sodium = (await import('libsodium-wrappers-sumo')).default
  await sodium.ready
  const workspaceKey = sodium.randombytes_buf(32)
  return sealKeyForSession(workspaceKey, serverSessionKey)
}

/**
 * Unwrap the raw 32-byte workspace key for direct client-side encryption.
 *
 * Unlike ``selectWorkspace`` (which returns the session-sealed wrapper for use
 * with ``/open``), this returns the plaintext key so the caller can feed it
 * into SecretBox ``encryptFile`` / ``createContentHash`` for the direct-upload
 * flow.
 *
 * The caller is responsible for keeping the returned key in memory only —
 * never log, persist, or transmit it over the wire.
 */
export async function getRawWorkspaceKey(
  arbi: ArbiClient,
  workspaceId: string,
  signingPrivateKeyBase64: string
): Promise<Uint8Array> {
  const { data: workspaces, error } = await arbi.fetch.GET('/v1/user/workspaces')
  if (error || !workspaces) {
    throw new ArbiError('Failed to fetch workspaces')
  }

  const ws = workspaces.find((w) => w.external_id === workspaceId)
  if (!ws || !ws.wrapped_key) {
    throw new ArbiError(`Workspace ${workspaceId} not found or has no encryption key`)
  }

  const signingPrivateKey = base64ToBytes(signingPrivateKeyBase64)
  const ed25519PublicKey = signingPrivateKey.slice(32, 64)
  const encryptionKeyPair = deriveEncryptionKeypairFromSigning({
    publicKey: ed25519PublicKey,
    secretKey: signingPrivateKey,
  })

  return sealedBoxDecrypt(ws.wrapped_key, encryptionKeyPair.secretKey)
}

/**
 * Find a workspace by ID in the user's workspace list, select it, and return workspace info.
 */
/**
 * Open a workspace returned by `GET /v1/user/workspaces`.
 *
 * Single entry point for both grant modes — the caller doesn't need to
 * branch on `wrapped_key`:
 *
 * - **Permanent grant** (`wrapped_key` set): unwrap with the user's
 *   signing-derived encryption keypair, re-seal for the server session,
 *   and POST that to `/open`. Returns the sealed key so callers can
 *   cache it.
 * - **Session-only grant** (`wrapped_key` null): the workspace key is
 *   already in the user's session entry (Redis, sealed for the session
 *   pubkey) and never lives on disk. Server unseals on `/open` with an
 *   empty body. Returns `null` because there's nothing for the client
 *   to cache.
 *
 * Used by PA-agent flows (pa_grant deposits the key into the session
 * and creates a `WorkspaceUsers` row with `wrapped_key=NULL`) and any
 * other session-scoped share via `add_workspace_users` with
 * `session_pubkey_b64`.
 */
async function openWorkspaceEntry(
  arbi: ArbiClient,
  ws: { external_id: string; wrapped_key?: string | null },
  serverSessionKey: Uint8Array,
  signingPrivateKeyBase64: string
): Promise<string | null> {
  if (ws.wrapped_key) {
    const sealedKey = await selectWorkspace(
      arbi,
      ws.external_id,
      ws.wrapped_key,
      serverSessionKey,
      signingPrivateKeyBase64
    )
    const { error } = await arbi.fetch.POST('/v1/workspace/{workspace_ext_id}/open', {
      params: { path: { workspace_ext_id: ws.external_id } },
      body: { workspace_key: sealedKey },
    })
    if (error) {
      throw new ArbiError(`Workspace open failed for ${ws.external_id}: ${JSON.stringify(error)}`)
    }
    return sealedKey
  }
  arbi.session.setSelectedWorkspace(ws.external_id)
  const { error } = await arbi.fetch.POST('/v1/workspace/{workspace_ext_id}/open', {
    params: { path: { workspace_ext_id: ws.external_id } },
    body: {},
  })
  if (error) {
    // Session-only grant flow — the most common cause is the
    // workspace key not being deposited in the session yet. Surface
    // the server message verbatim so the user sees the actionable hint
    // (e.g. "open the workspace and retry").
    throw new ArbiError(`Workspace open failed for ${ws.external_id}: ${JSON.stringify(error)}`)
  }
  return null
}

export async function selectWorkspaceById(
  arbi: ArbiClient,
  workspaceId: string,
  serverSessionKey: Uint8Array,
  signingPrivateKeyBase64: string
): Promise<{
  external_id: string
  name: string
  wrapped_key: string | null
  /** The session-sealed workspace key — `null` for session-only grants */
  sealed_key: string | null
}> {
  const { data: workspaces, error } = await arbi.fetch.GET('/v1/user/workspaces')
  if (error || !workspaces) {
    throw new ArbiError('Failed to fetch workspaces')
  }
  const ws = workspaces.find((w) => w.external_id === workspaceId)
  if (!ws) {
    throw new ArbiError(`Workspace ${workspaceId} not found`)
  }
  const sealedKey = await openWorkspaceEntry(arbi, ws, serverSessionKey, signingPrivateKeyBase64)
  return {
    external_id: ws.external_id,
    name: ws.name,
    wrapped_key: ws.wrapped_key ?? null,
    sealed_key: sealedKey,
  }
}

/**
 * Predicate: is the cached access token still within its client-side TTL?
 *
 * This is **session-only** — no workspace coupling. The backend stores
 * sessions as the long-lived auth unit (Redis entry keyed by session
 * pubkey, referenced by the JWT's ``sk`` claim) and the same session
 * can hold deposits for many workspaces simultaneously. So the question
 * "is my session still alive?" is independent of which workspace any
 * given command is going to hit.
 *
 * A previous version of this check required ``creds.workspaceId ===
 * workspaceId``, which forced a re-login whenever the CLI switched
 * workspaces — and re-login mints a brand-new server session, which
 * silently **orphans** any session-only workspace deposits (PA agent
 * grants, temporary shares) that lived on the old session. The fix is
 * to gate strictly on token aliveness; workspace state is tracked
 * separately via ``creds.openedWorkspaces``.
 */
function isSessionAlive(
  creds: CliCredentials
): creds is CliCredentials & { accessToken: string; tokenTimestamp: string } {
  return !!(
    creds.accessToken &&
    creds.tokenTimestamp &&
    Date.now() - new Date(creds.tokenTimestamp).getTime() < TOKEN_MAX_AGE_MS
  )
}

/**
 * Reconstruct an authenticated SDK client from cached credentials,
 * without a server round-trip.
 *
 * Used when ``isSessionAlive(creds)`` is true: the JWT in
 * ``creds.accessToken`` still authorises us against the live server
 * session, so we can wire up the client locally and skip
 * ``loginWithKey``. Crucially this preserves the existing session —
 * a fresh ``loginWithKey`` would mint a new one and orphan its
 * deposits.
 */
async function buildAuthFromCache(
  config: CliConfig,
  creds: CliCredentials & { accessToken: string },
  store: ConfigStore
): Promise<AuthenticatedClient> {
  const arbi = createArbiClient(buildClientOptions(config, store, creds.email))
  await arbi.crypto.initSodium()

  // Restore the same identity ``loginWithKey`` would have set — both
  // email **and** userExtId. ``arbi dm list`` (and any feature that
  // reads ``arbi.session.getState().userExtId``) hard-fails when the
  // ext_id is missing, so passing only the email here would break the
  // fast path silently for cached sessions.
  arbi.session.setUser(creds.email, creds.userExtId)
  arbi.session.setAccessToken(creds.accessToken)

  const signingPrivateKey = base64ToBytes(creds.signingPrivateKeyBase64)
  const serverSessionKey = base64ToBytes(creds.serverSessionKeyBase64)
  const loginResult: LoginResult = {
    accessToken: creds.accessToken,
    userExtId: creds.userExtId,
    signingPrivateKey,
    serverSessionKey,
  }
  return { arbi, loginResult }
}

/**
 * Authenticate and return the SDK client + config.
 *
 * Fast path: reuse the live server session via cached accessToken.
 * Slow path: fresh ``loginWithKey`` (mints a new session, clears
 * ``openedWorkspaces``).
 */
export async function resolveAuth(store: ConfigStore): Promise<AuthContext> {
  const config = store.requireConfig()
  const creds = store.requireCredentials()
  if (isSessionAlive(creds)) {
    const { arbi, loginResult } = await buildAuthFromCache(config, creds, store)
    return { arbi, loginResult, config }
  }
  const { arbi, loginResult } = await createAuthenticatedClient(config, creds, store)
  return { arbi, loginResult, config }
}

/**
 * Authenticate, ensure the workspace's key is deposited on the current
 * server session, and return everything callers need.
 *
 * Decisions are independent:
 *  1. **Session aliveness** — reuse the live session if its accessToken
 *     is within TTL; otherwise fresh login. (Fresh login = new session
 *     = ``openedWorkspaces`` reset to ``[]``.)
 *  2. **Workspace deposit** — if ``workspaceId`` already appears in
 *     ``creds.openedWorkspaces``, the server's session already holds
 *     its key; no ``/open`` needed. Otherwise call ``/open`` once and
 *     append to the set.
 *
 * Splitting these matters: previously the cache was gated on
 * ``creds.workspaceId === workspaceId``, so any switch between
 * workspaces forced a re-login. That orphaned PA-agent session
 * deposits and broke ``arbi docs`` for them. Now switching workspaces
 * within a live session just adds another entry to
 * ``openedWorkspaces`` — no re-login, no orphaned deposits.
 */
export async function resolveWorkspace(
  store: ConfigStore,
  workspaceOpt?: string
): Promise<WorkspaceContext> {
  const config = store.requireConfig()
  const creds = store.requireCredentials()
  const workspaceId = workspaceOpt || config.selectedWorkspaceId

  if (!workspaceId) {
    throw new ArbiError('No workspace selected. Run: arbi workspace select <id>')
  }

  if (isSessionAlive(creds)) {
    const { arbi, loginResult } = await buildAuthFromCache(config, creds, store)
    arbi.session.setSelectedWorkspace(workspaceId)

    // Always call /open, even if the workspace is already in
    // ``openedWorkspaces``. The backend's ``session.active_workspace``
    // is a **single value** that only ``/open`` mutates — every
    // workspace-scoped endpoint reads it (see ``get_workspace_context``
    // in ``api/v1/routers/dependencies.py``). If a prior command on
    // this session opened a *different* workspace, ``active_workspace``
    // is now stuck on that one; skipping ``/open`` here would send
    // every subsequent request to the wrong workspace.
    //
    // The cache field is still maintained for diagnostics (and for the
    // PA-agent path, which pre-populates it so the backend's
    // post-deposit state is reflected). But it doesn't gate the call.
    await selectWorkspaceById(
      arbi,
      workspaceId,
      loginResult.serverSessionKey,
      creds.signingPrivateKeyBase64
    )

    const nextOpened = Array.from(new Set([...(creds.openedWorkspaces ?? []), workspaceId]))
    store.saveCredentials({
      ...store.requireCredentials(),
      openedWorkspaces: nextOpened,
    })

    return {
      arbi,
      loginResult,
      config,
      workspaceId,
      accessToken: creds.accessToken,
    }
  }

  // Session expired / never logged in — fresh login.
  // ``createAuthenticatedClient`` clears ``openedWorkspaces`` for us.
  const { arbi, loginResult } = await createAuthenticatedClient(config, creds, store)
  await selectWorkspaceById(
    arbi,
    workspaceId,
    loginResult.serverSessionKey,
    creds.signingPrivateKeyBase64
  )
  const accessToken = arbi.session.getState().accessToken
  if (!accessToken) {
    throw new ArbiError('Authentication error — missing token')
  }
  store.saveCredentials({
    ...store.requireCredentials(),
    accessToken,
    tokenTimestamp: new Date().toISOString(),
    openedWorkspaces: [workspaceId],
  })
  return {
    arbi,
    loginResult,
    config,
    workspaceId,
    accessToken,
  }
}
